import { Section } from '@ria-client/tw-shared-ui/layout/section';

import { FeeStructureTable } from './FeeStructureTable';
import { SectionHeader } from './SectionHeader';
import { BookIcon } from './icons';

export function KeyTermsSection() {
  return (
    <Section aria-label="Key financial advisor terms" bg="paper" bordered>
      <SectionHeader
        icon={<BookIcon className="size-4.5" />}
        gradient="yellow"
        title="Key terms to know"
        subtitle="Investment advisor websites — and their required SEC filings — use a lot of jargon. Understanding these concepts will help you cut through the noise, evaluate the financial advisors and make an informed decision about which is best for you."
      />
      <div className="space-y-7">
        <div>
          <h3 className="typography-h3">Fiduciary</h3>
          <p className="typography-body">
            A fiduciary is an individual or organization that has a legal duty to act in the best
            financial interests of someone else, placing the client’s financial welfare above their
            own. Ask any prospective service provider if they're a fiduciary, and verify their
            regulatory and professional status. Fiduciary duties depend on the profession and any
            regulations surrounding the role. For example, board members may have certain fiduciary
            duties to the companies that they advise. Trustees owe fiduciary duties when they manage
            assets for their beneficiaries.
          </p>
        </div>
        <div>
          <h3 className="typography-h3">Fee-Only vs. Fee-Based</h3>
          <p className="typography-body">
            Fee-only advisors are compensated solely by client fees. A fee-based financial planner
            is paid by the client but may also receive commissions from other companies for selling
            certain financial products. This can create a conflict of interest, because a fee-based
            advisor may charge you for advice while potentially steering you toward investment
            products that personally benefit them. Choosing a fee-only financial planner who follows
            the fiduciary standard is usually a better choice for most investors. Several
            professional groups require members to abide by the fiduciary standard, including The
            National Association of Personal Financial Advisors, Garrett Planning Network, XY
            Planning Network and the Alliance of Comprehensive Planners.
          </p>
        </div>
        <div>
          <h3 className="typography-h3">Form ADV</h3>
          <p className="typography-body">
            Form ADV (&ldquo;the Uniform Application for Investment Advisor Registration&rdquo;) is
            one of the most important documents to check if you&rsquo;re going to hire an investment
            adviser (or already have one). This free disclosure document reveals everything from
            information about an advisor&rsquo;s fee structure and firm history to their management
            style and any misconduct. If an investment advisor receives compensation for
            recommending securities products, you&rsquo;ll see the relationship disclosed in Form
            ADV. If they are paid based on a percentage of assets managed or a flat-rate for one-off
            services, that will be spelled out.
          </p>
        </div>
        <div>
          <h3 className="typography-h3">Assets Under Management (AUM)</h3>
          <p className="typography-body">
            Assets under management (AUM) are securities portfolios (including, for example, stocks,
            bonds, mutual funds and ETFs) for which an investment advisor provides continuous and
            regular supervisory or management services. As a client, your AUM is the amount that is
            being managed by your investment advisor. When evaluating investment advisors, AUM is
            used mostly as a scale and business-quality metric, not necessarily a direct measure of
            investment skill. Investment advisors may also use it to set their fees. Higher AUM
            often signals more clients, institutional credibility, longer operating history and
            operational maturity. AUM fees are one of the most common fees financial advisory firm
            charge, alongside flat fees and hourly fees. You usually don&rsquo;t pay all three fees.
            You may also encounter other fees when you hire a financial advisor.
          </p>
          <p className="typography-body">
            Investment advisors, broker dealers and financial planners have different ways of
            charging for their services.
          </p>
          <FeeStructureTable />
        </div>
        <div>
          <h3 className="typography-h3">SEC vs. State Registration</h3>
          <p className="typography-body">
            In general, state authorities regulate firms with less than $100 million in AUM; the SEC
            regulates firms with $100 million or more in AUM. There are some exceptions. Both must
            file Form ADV.
          </p>
        </div>
        <div>
          <h3 className="typography-h3">Wrap Fees</h3>
          <p className="typography-body">
            A wrap fee is a bundled investment advisory fee where a client pays one combined fee
            that &ldquo;wraps&rdquo; together multiple services and costs (advisory fees, brokerage
            fees, execution charges and custodial fees) into a single charge, usually based on AUM.
            Wrap fee programs can be called by several different names, including asset allocation
            programs, asset management programs, investment management programs, mini-accounts,
            uniform managed accounts and separately managed accounts. Wrap fees can save you money
            under the right circumstances, but they can also create conflicts of interest for your
            advisor.
          </p>
        </div>
      </div>
    </Section>
  );
}
